import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Container,
  Paper,
  Alert,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "./Toast";
import axios from "axios";
import { useFirebaseAuth } from "../hooks/useFirebaseAuth";
import { auth } from "../firebase/config";
import { signOut } from "firebase/auth";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { getValidToken } from "../utils/authUtils";
import { useQueryClient } from "@tanstack/react-query";
import { handleLogout } from "../utils/utils";

interface FormData {
  name: string;
  phoneNumber: string;
  email: string;
  companyName: string;
  message: string;
  numberOfVehicles: number;
}

interface RegistrationStatus {
  status: "pending" | "approved" | "rejected" | null;
  message: string;
}

// Validation schema using Yup
const validationSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Name must be at least 2 characters")
    .required("Name is required"),
  phoneNumber: Yup.string()
    .matches(/^\d{10}$/, "Please enter a valid 10-digit phone number")
    .required("Phone number is required"),
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Email is required"),
  companyName: Yup.string()
    .min(2, "Company name must be at least 2 characters")
    .required("Company name is required"),
  numberOfVehicles: Yup.number()
    .min(1, "Number of vehicles must be at least 1")
    .required("Number of vehicles is required"),
  message: Yup.string(),
});

const NotRegistered: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useFirebaseAuth();
  const queryClient = useQueryClient();
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [registrationStatus, setRegistrationStatus] =
    useState<RegistrationStatus>({
      status: null,
      message: "",
    });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchParams] = useSearchParams();
  const [initialFormValues, setInitialFormValues] = useState<FormData>({
    name: "",
    phoneNumber: "",
    email: "",
    companyName: "",
    message: "",
    numberOfVehicles: 0,
  });
  const isMobileExistingUser = searchParams.get("mobile") === "true";

  useEffect(() => {
    const checkRegistrationStatus = async () => {
      if (!user) return;
      const userPhoneNumber = user.phoneNumber?.slice(-10) || "";

      // Update initial form values with user's phone number
      setInitialFormValues((prev) => ({
        ...prev,
        phoneNumber: userPhoneNumber,
      }));

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_HOST}contact/request/${user.phoneNumber}`
        );

        if (response.data?.isSuccess) {
          const status = response.data.data?.status?.toLowerCase();
          setRegistrationStatus({
            status: status,
            message:
              status === "pending"
                ? "Your request is pending approval. We will contact you soon."
                : status === "approved"
                ? "Your request has been approved! Please login to continue."
                : status === "rejected"
                ? "Your request was not approved. Please contact support for more information."
                : "",
          });

          if (status === "pending" || status === "rejected") {
            showToast(
              "You have already submitted a request. We will get back to you soon.",
              "info"
            );
          } else if (status === "approved") {
            showToast("Request approved! Please login to continue.", "success");
          }
        }
      } catch (error) {
        console.error("Error checking registration status:", error);
      } finally {
        setCheckingStatus(false);
      }
    };

    checkRegistrationStatus();
  }, [user]);

  const handleSubmit = async (values: FormData, { setSubmitting }: any) => {
    try {
      // Use fetch API to bypass any axios configurations
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}contact/register`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(values),
        }
      );

      const responseData = await response.json();

      if (responseData?.isSuccess) {
        showToast(
          "Thank you for your interest! We will review your request and contact you soon.",
          "success"
        );
        setRegistrationStatus({
          status: "pending",
          message:
            "You have already submitted a request. We will get back to you soon.",
        });
      } else {
        throw new Error("Failed to submit registration request");
      }
    } catch (error) {
      console.error("Error submitting registration:", error);
      showToast(
        "Failed to submit registration request. Please try again.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleFreeTrial = async (values: FormData, { setSubmitting }: any) => {
    let companyId: string | null = null;

    try {
      setSubmitting(true);
      const token = await getValidToken();

      // Step 1: Create company
      const companyResponse = await axios.post(
        `${process.env.REACT_APP_API_HOST}company`,
        {
          name: values.companyName,
          phoneNumber: values.phoneNumber,
          fleetSize: values.numberOfVehicles,
          verificationStatus: "PENDING",
          planType: "FREE TRIAL",
          planExpiry: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000),
          primaryContactName: values.name,
          primaryContactPhone: values.phoneNumber,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!companyResponse.data?.isSuccess || !companyResponse.data?.data?.id) {
        throw new Error(
          companyResponse.data?.message || "Failed to create company"
        );
      }

      companyId = companyResponse.data.data.id;

      // Step 2: Submit registration request
      const registrationResponse = await fetch(
        `${process.env.REACT_APP_API_HOST}contact/register`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...values,
            companyId: companyId, // Include the created company ID
          }),
        }
      );

      const registrationData = await registrationResponse.json();

      if (registrationData?.isSuccess) {
        showToast(
          "Free trial request submitted successfully! Our team will review and contact you soon.",
          "success"
        );
        setRegistrationStatus({
          status: "pending",
          message:
            "Your free trial request is pending approval. We will contact you soon.",
        });
      } else {
        throw new Error("Failed to submit free trial request");
      }
    } catch (error: any) {
      console.error("Error during free trial setup:", error);

      // Provide specific error messages based on what failed
      if (companyId && error.message?.includes("registration")) {
        showToast(
          "There was an issue submitting your free trial request. Please contact support.",
          "error"
        );
      } else if (error.message?.includes("company")) {
        showToast("Please check your details and try again.", "error");
      } else {
        showToast(
          error.message || "Failed to process your request. Please try again.",
          "error"
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (checkingStatus) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Registration form header */}
        <Typography variant="h5" gutterBottom align="center">
          Welcome to Gaadi Kharcha
        </Typography>

        {isMobileExistingUser ? (
          <>
            <Typography
              variant="h6"
              gutterBottom
              align="center"
              color="primary"
            >
              Mobile Access Limited
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={500} gutterBottom>
                Dashboard Access on Desktop Only
              </Typography>
              <Typography variant="body2">
                You are successfully logged in! However, the full dashboard
                functionality is only available on desktop/laptop computers.
                Please use a desktop or laptop to access all features and manage
                your fleet.
              </Typography>
            </Alert>
            <Button
              variant="contained"
              onClick={() => handleLogout(queryClient)}
              fullWidth
            >
              Logout
            </Button>
          </>
        ) : (
          <>
            {registrationStatus.status && (
              <>
                <Alert
                  severity={
                    registrationStatus.status === "approved"
                      ? "success"
                      : registrationStatus.status === "rejected"
                      ? "error"
                      : "info"
                  }
                  sx={{ mb: 3 }}
                >
                  {registrationStatus.message}
                </Alert>
                <Button
                  variant="contained"
                  onClick={() => handleLogout(queryClient)}
                  fullWidth
                >
                  Logout
                </Button>
              </>
            )}
            {registrationStatus.status === null && (
              <>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 4 }}
                  align="center"
                >
                  Thank you for your interest in joining our platform. Please
                  complete the registration form below with your business
                  details. You can start with a free trial and upgrade to a paid
                  plan later. You can also contact us at{" "}
                  <a href="mailto:info@aequitous.com">info@aequitous.com</a> for
                  any questions.
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 4 }}
                  align="center"
                >
                  {isMobile && (
                    <>
                      <br />
                      <br />
                      <strong>Note:</strong> You can register on mobile, but the
                      full dashboard will be available only on desktop/laptop.
                    </>
                  )}
                </Typography>

                <Formik
                  validationSchema={validationSchema}
                  initialValues={initialFormValues}
                  enableReinitialize={true}
                  onSubmit={handleFreeTrial}
                >
                  {({
                    errors,
                    touched,
                    isSubmitting,
                    values,
                    setFieldValue,
                  }) => (
                    <Form>
                      <Field
                        fullWidth
                        label="Name"
                        name="name"
                        as={TextField}
                        margin="normal"
                        required
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                      />

                      <Field
                        fullWidth
                        label="Phone Number"
                        name="phoneNumber"
                        as={TextField}
                        margin="normal"
                        required
                        inputProps={{
                          maxLength: 10,
                          pattern: "[0-9]*",
                          inputMode: "numeric",
                        }}
                        error={
                          touched.phoneNumber && Boolean(errors.phoneNumber)
                        }
                        helperText={touched.phoneNumber && errors.phoneNumber}
                      />

                      <Field
                        fullWidth
                        label="Email"
                        name="email"
                        type="email"
                        as={TextField}
                        margin="normal"
                        required
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                      />

                      <Field
                        fullWidth
                        label="Company Name"
                        name="companyName"
                        as={TextField}
                        margin="normal"
                        required
                        error={
                          touched.companyName && Boolean(errors.companyName)
                        }
                        helperText={touched.companyName && errors.companyName}
                      />

                      <Field
                        fullWidth
                        label="Number of Vehicles"
                        name="numberOfVehicles"
                        as={TextField}
                        margin="normal"
                        required
                        type="number"
                        inputProps={{
                          min: 1,
                          step: 1,
                        }}
                        error={
                          touched.numberOfVehicles &&
                          Boolean(errors.numberOfVehicles)
                        }
                        helperText={
                          touched.numberOfVehicles && errors.numberOfVehicles
                        }
                      />

                      <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                        <Button
                          variant="outlined"
                          onClick={() => handleLogout(queryClient)}
                          fullWidth
                          disabled={isSubmitting}
                        >
                          Logout
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          fullWidth
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <CircularProgress size={24} />
                          ) : (
                            "Submit Request"
                          )}
                        </Button>
                      </Box>
                    </Form>
                  )}
                </Formik>
              </>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
};

export default NotRegistered;
